import type { CSSProperties } from 'react';
import { WELL_COLORS, type ColorToken } from '../lib/colors';
import { WELL_SPACING_PX, type WellSpacingToken } from '../lib/spacing';
import { StructuredContentRenderer } from '../renderer';
import type { NodeProps, StructuredContent } from '../types';

export type WellProps = NodeProps<{
  margin?: WellSpacingToken;
  padding?: WellSpacingToken;
  marginTop?: WellSpacingToken;
  marginBottom?: WellSpacingToken;
  marginLeft?: WellSpacingToken;
  marginRight?: WellSpacingToken;
  paddingTop?: WellSpacingToken;
  paddingBottom?: WellSpacingToken;
  paddingLeft?: WellSpacingToken;
  paddingRight?: WellSpacingToken;
  backgroundColor?: ColorToken;
  color?: ColorToken;
  children: StructuredContent | StructuredContent[];
}>;

export function Well({ props }: WellProps) {
  const margin = WELL_SPACING_PX[props.margin ?? '0'];
  const padding = WELL_SPACING_PX[props.padding ?? '0'];
  const marginTop = WELL_SPACING_PX[props.marginTop ?? '0'];
  const marginBottom = WELL_SPACING_PX[props.marginBottom ?? '0'];
  const marginLeft = WELL_SPACING_PX[props.marginLeft ?? '0'];
  const marginRight = WELL_SPACING_PX[props.marginRight ?? '0'];
  const paddingTop = WELL_SPACING_PX[props.paddingTop ?? '0'];
  const paddingBottom = WELL_SPACING_PX[props.paddingBottom ?? '0'];
  const paddingLeft = WELL_SPACING_PX[props.paddingLeft ?? '0'];
  const paddingRight = WELL_SPACING_PX[props.paddingRight ?? '0'];

  const style: CSSProperties & Record<`--well-${string}`, string> = {
    backgroundColor: WELL_COLORS[props.backgroundColor ?? props.color ?? 'green-lighter'],
  };
  if (typeof margin === 'number') style.margin = margin;
  if (typeof padding === 'number') style.padding = padding;
  if (typeof marginTop === 'number') style.marginTop = marginTop;
  if (typeof marginBottom === 'number') style.marginBottom = marginBottom;
  if (typeof marginLeft === 'number') style.marginLeft = marginLeft;
  if (typeof marginRight === 'number') style.marginRight = marginRight;
  if (typeof paddingTop === 'number') style.paddingTop = paddingTop;
  if (typeof paddingBottom === 'number') style.paddingBottom = paddingBottom;
  if (typeof paddingLeft === 'number') style.paddingLeft = paddingLeft;
  if (typeof paddingRight === 'number') style.paddingRight = paddingRight;
  if (typeof margin === 'object') style['--well-margin'] = `${margin.md}px`;
  if (typeof padding === 'object') style['--well-padding'] = `${padding.md}px`;
  if (typeof marginTop === 'object') style['--well-margin-top'] = `${marginTop.md}px`;
  if (typeof marginBottom === 'object') style['--well-margin-bottom'] = `${marginBottom.md}px`;
  if (typeof marginLeft === 'object') style['--well-margin-left'] = `${marginLeft.md}px`;
  if (typeof marginRight === 'object') style['--well-margin-right'] = `${marginRight.md}px`;
  if (typeof paddingTop === 'object') style['--well-padding-top'] = `${paddingTop.md}px`;
  if (typeof paddingBottom === 'object') style['--well-padding-bottom'] = `${paddingBottom.md}px`;
  if (typeof paddingLeft === 'object') style['--well-padding-left'] = `${paddingLeft.md}px`;
  if (typeof paddingRight === 'object') style['--well-padding-right'] = `${paddingRight.md}px`;

  const responsiveClassName = [
    typeof margin === 'object' && 'md:!m-[var(--well-margin)]',
    typeof padding === 'object' && 'md:!p-[var(--well-padding)]',
    typeof marginTop === 'object' && 'md:!mt-[var(--well-margin-top)]',
    typeof marginBottom === 'object' && 'md:!mb-[var(--well-margin-bottom)]',
    typeof marginLeft === 'object' && 'md:!ml-[var(--well-margin-left)]',
    typeof marginRight === 'object' && 'md:!mr-[var(--well-margin-right)]',
    typeof paddingTop === 'object' && 'md:!pt-[var(--well-padding-top)]',
    typeof paddingBottom === 'object' && 'md:!pb-[var(--well-padding-bottom)]',
    typeof paddingLeft === 'object' && 'md:!pl-[var(--well-padding-left)]',
    typeof paddingRight === 'object' && 'md:!pr-[var(--well-padding-right)]',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={responsiveClassName} style={style}>
      <StructuredContentRenderer doc={props.children} />
    </div>
  );
}
